import { create } from 'zustand';
import { fetchProject, fetchMateriaallijst as fetchMateriaallijstApi, fetchCollectoren as fetchCollectorenApi, uploadMateriaallijst as uploadMateriaallijstApi, deleteMateriaallijst as deleteMateriaallijstApi, searchDocuments as searchDocumentsApi, listDocuments as listDocumentsApi, updateTaskPlanningStage as updateTaskPlanningStageApi, updateProjectNotes as updateProjectNotesApi } from '@/api/tasks';
import type { CollectorPicking, PlanningStage } from '@/api/tasks';
import type { ProjectDetail, MateriaalLine, StockPicking, StockMove, Collector, SharePointItem } from '@/types/odoo';

export type TabName = 'installateur_taak' | 'offerte' | 'planning' | 'materiaal' | 'documenten' | 'inkooporders' | 'dashboard';

interface ProjectViewState {
  detail: ProjectDetail | null;
  loading: boolean;
  error: string | null;
  activeTab: TabName;
  activeTaskId: number | null;
  /** Counts the tasks the user chose (a click or a ?task= link); 0 while the active
   *  task is the one ProjectView auto-selected on open. */
  taskChoice: number;
  materiaalLines: MateriaalLine[] | null;
  materiaalPickings: StockPicking[] | null;
  materiaalMoves: StockMove[] | null;
  materiaalProjectId: number | null;
  materiaalLoading: boolean;
  materiaalError: string | null;
  uploadingMateriaal: boolean;
  uploadError: string | null;
  collectors: Collector[] | null;
  collectorInstallerNames: Record<string, string> | null;
  collectorAllInstallerNames: Record<string, string> | null;
  collectorPickings: CollectorPicking[] | null;
  collectorProjectId: number | null;
  collectorLoading: boolean;
  collectorError: string | null;
  documentItems: SharePointItem[] | null;
  documentPath: string | null;
  documentBreadcrumb: string[] | null;
  documentFound: boolean | null;
  documentProjectId: number | null;
  documentLoading: boolean;
  documentError: string | null;
  documentCache: Map<string, { items: SharePointItem[]; breadcrumb: string[] }>;
  fetchDocumentSearch: (projectId: number) => Promise<void>;
  fetchDocumentList: (projectId: number, path: string) => Promise<void>;
  preloadDocumentFolders: (projectId: number, items: SharePointItem[]) => void;
  fetchProject: (projectId: number) => Promise<void>;
  refreshProject: (projectId: number) => Promise<void>;
  fetchMateriaallijst: (projectId: number) => Promise<void>;
  uploadMateriaallijst: (projectId: number, taskId: number, file: File) => Promise<boolean>;
  deleteMateriaallijst: (projectId: number, pickingId: number) => Promise<boolean>;
  fetchCollectoren: (projectId: number) => Promise<void>;
  updateCollectorFotoStatus: (collectorId: number, status: string) => void;
  updateTaskPlanningStage: (projectId: number, taskId: number, stage: PlanningStage) => Promise<boolean>;
  applyTaskDescription: (projectId: number, taskId: number, descriptionHtml: string) => void;
  updateNotes: (projectId: number, notes: string) => Promise<boolean>;
  setActiveTab: (tab: TabName) => void;
  setActiveTask: (id: number, auto?: boolean) => void;
}

export const useProjectViewStore = create<ProjectViewState>((set) => ({
  detail: null,
  loading: false,
  error: null,
  activeTab: 'offerte',
  activeTaskId: null,
  taskChoice: 0,
  materiaalLines: null,
  materiaalPickings: null,
  materiaalMoves: null,
  materiaalProjectId: null,
  materiaalLoading: false,
  materiaalError: null,
  uploadingMateriaal: false,
  uploadError: null,
  collectors: null,
  collectorInstallerNames: null,
  collectorAllInstallerNames: null,
  collectorPickings: null,
  collectorProjectId: null,
  collectorLoading: false,
  collectorError: null,
  documentItems: null,
  documentPath: null,
  documentBreadcrumb: null,
  documentFound: null,
  documentProjectId: null,
  documentLoading: false,
  documentError: null,
  documentCache: new Map(),

  // NB: the reset below clears `collectorError` too. Without it, navigating from a
  // project whose collector fetch failed paints one frame of the NEW project's
  // header carrying the OLD project's error ("—" / "niet beschikbaar") before its
  // own fetch starts. Every other *Error field is cleared by its own fetcher on
  // entry; this one has to be cleared here because the collectoren are fetched by
  // a different component, later.
  fetchProject: async (projectId: number) => {
    set({ detail: null, loading: true, error: null, materiaalLines: null, materiaalPickings: null, materiaalMoves: null, materiaalProjectId: null, collectors: null, collectorInstallerNames: null, collectorAllInstallerNames: null, collectorPickings: null, collectorProjectId: null, collectorError: null, documentItems: null, documentPath: null, documentBreadcrumb: null, documentFound: null, documentProjectId: null, documentCache: new Map() });
    try {
      const detail = await fetchProject(projectId);
      set({ detail, loading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Projectgegevens ophalen mislukt';
      set({ error: message, loading: false });
    }
  },

  refreshProject: async (projectId: number) => {
    try {
      const detail = await fetchProject(projectId);
      set({ detail });
    } catch {
      // silent — keep existing data on failure
    }
  },

  fetchMateriaallijst: async (projectId: number) => {
    set({ materiaalLoading: true, materiaalError: null });
    try {
      const { lines, pickings, moves } = await fetchMateriaallijstApi(projectId);
      set({ materiaalLines: lines, materiaalPickings: pickings, materiaalMoves: moves ?? null, materiaalProjectId: projectId, materiaalLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Materiaallijsten ophalen mislukt';
      set({ materiaalError: message, materiaalLoading: false });
    }
  },

  uploadMateriaallijst: async (projectId: number, taskId: number, file: File): Promise<boolean> => {
    set({ uploadingMateriaal: true, uploadError: null });
    try {
      await uploadMateriaallijstApi(projectId, taskId, file);
      // Reset materiaal data to force re-fetch
      set({ uploadingMateriaal: false, materiaalLines: null, materiaalPickings: null, materiaalMoves: null, materiaalProjectId: null, collectorPickings: null, collectorProjectId: null });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload mislukt';
      set({ uploadError: message, uploadingMateriaal: false });
      return false;
    }
  },

  deleteMateriaallijst: async (projectId: number, pickingId: number): Promise<boolean> => {
    try {
      await deleteMateriaallijstApi(projectId, pickingId);
      // Reset materiaal data to force re-fetch (same pattern as upload)
      set({ materiaalLines: null, materiaalPickings: null, materiaalMoves: null, materiaalProjectId: null });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verwijderen mislukt';
      set({ uploadError: message });
      return false;
    }
  },

  fetchCollectoren: async (projectId: number) => {
    set({ collectorLoading: true, collectorError: null });
    try {
      const { collectors, installerNames, allInstallerNames, pickings } = await fetchCollectorenApi(projectId);
      set({ collectors, collectorInstallerNames: installerNames, collectorAllInstallerNames: allInstallerNames ?? null, collectorPickings: pickings ?? null, collectorProjectId: projectId, collectorLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Collectoren ophalen mislukt';
      set({ collectorError: message, collectorLoading: false });
    }
  },

  fetchDocumentSearch: async (projectId: number) => {
    set({ documentLoading: true, documentError: null, documentCache: new Map() });
    try {
      const { found, path, items, breadcrumb } = await searchDocumentsApi(projectId);
      const cache = new Map<string, { items: SharePointItem[]; breadcrumb: string[] }>();
      if (path) cache.set(path, { items, breadcrumb });
      set({ documentItems: items, documentPath: path, documentBreadcrumb: breadcrumb, documentFound: found, documentProjectId: projectId, documentLoading: false, documentCache: cache });
      // Preload subfolders in background
      if (found) useProjectViewStore.getState().preloadDocumentFolders(projectId, items);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'SharePoint documenten ophalen mislukt';
      set({ documentError: message, documentLoading: false });
    }
  },

  fetchDocumentList: async (projectId: number, path: string) => {
    // Show cached data instantly if available
    const cached = useProjectViewStore.getState().documentCache.get(path);
    if (cached) {
      set({ documentItems: cached.items, documentPath: path, documentBreadcrumb: cached.breadcrumb, documentLoading: false });
    } else {
      set({ documentLoading: true, documentError: null });
    }
    // Always fetch fresh data in background
    try {
      const { items, breadcrumb } = await listDocumentsApi(projectId, path);
      const cache = new Map(useProjectViewStore.getState().documentCache);
      cache.set(path, { items, breadcrumb });
      set({ documentItems: items, documentPath: path, documentBreadcrumb: breadcrumb, documentLoading: false, documentCache: cache });
      // Preload subfolders in background
      useProjectViewStore.getState().preloadDocumentFolders(projectId, items);
    } catch (err) {
      // Only show error if we had no cached data
      if (!cached) {
        const message = err instanceof Error ? err.message : 'Map ophalen mislukt';
        set({ documentError: message, documentLoading: false });
      }
    }
  },

  preloadDocumentFolders: (projectId: number, items: SharePointItem[]) => {
    const cache = useProjectViewStore.getState().documentCache;
    const folders = items.filter((i) => i.type === 'folder' && !cache.has(i.path));
    // Only preload when there are a reasonable number of subfolders (project-level, not year-level)
    if (folders.length === 0 || folders.length > 15) return;
    // Sequential preload with small delay to avoid hammering PA
    let i = 0;
    const next = () => {
      if (i >= folders.length) return;
      const folder = folders[i++];
      listDocumentsApi(projectId, folder.path)
        .then(({ items: subItems, breadcrumb }) => {
          const freshCache = new Map(useProjectViewStore.getState().documentCache);
          freshCache.set(folder.path, { items: subItems, breadcrumb });
          set({ documentCache: freshCache });
        })
        .catch(() => { /* silently ignore preload failures */ })
        .finally(() => { setTimeout(next, 200); }); // 200ms between each preload
    };
    next();
  },

  updateCollectorFotoStatus: (collectorId: number, status: string) => {
    const current = useProjectViewStore.getState().collectors;
    if (!current) return;
    useProjectViewStore.setState({
      collectors: current.map((c) =>
        c.id === collectorId ? { ...c, x_studio_fotos_uitvoering: status } : c,
      ),
    });
  },

  updateTaskPlanningStage: async (projectId: number, taskId: number, stage: PlanningStage): Promise<boolean> => {
    const detail = useProjectViewStore.getState().detail;
    if (!detail) return false;

    const prevStage = detail.projectTasks.find((t) => t.id === taskId)?.x_studio_planningstage;

    // Optimistic update
    useProjectViewStore.setState({
      detail: {
        ...detail,
        projectTasks: detail.projectTasks.map((t) =>
          t.id === taskId ? { ...t, x_studio_planningstage: stage } : t,
        ),
      },
    });

    try {
      await updateTaskPlanningStageApi(projectId, taskId, stage);
      return true;
    } catch (err) {
      // Revert on failure
      const currentDetail = useProjectViewStore.getState().detail;
      if (currentDetail) {
        useProjectViewStore.setState({
          detail: {
            ...currentDetail,
            projectTasks: currentDetail.projectTasks.map((t) =>
              t.id === taskId ? { ...t, x_studio_planningstage: prevStage ?? false } : t,
            ),
          },
        });
      }
      const message = err instanceof Error ? err.message : 'Planningstage bijwerken mislukt';
      set({ uploadError: message });
      return false;
    }
  },

  // Sync a saved task description into the cached detail. The editors
  // (TaskDetailPanel, InstallateurTaakTab) own the API call + save timing; this is
  // a pure cache patch. Without it the editor resets to the pre-save text after
  // a task/fase switch — the save landed in Odoo but the dashboard showed stale
  // text ("zegt opgeslagen maar bewaart niet"). Guarded on detail.projectId so
  // a save that resolves after navigating to another project never mutates it.
  applyTaskDescription: (projectId: number, taskId: number, descriptionHtml: string) => {
    const detail = useProjectViewStore.getState().detail;
    if (!detail || detail.projectId !== projectId) return;
    useProjectViewStore.setState({
      detail: {
        ...detail,
        projectTasks: detail.projectTasks.map((t) =>
          t.id === taskId ? { ...t, description: descriptionHtml } : t,
        ),
      },
    });
  },

  // Dashboard notes autosave (Fase 4). Optimistic sync of detail.project so the
  // value survives a tab-switch remount; rollback on failure. Conflict =
  // last-write-wins. Both the optimistic write AND the rollback are guarded on
  // detail.projectId === projectId: a save fired for project 416 must never
  // mutate project 999's record after the user has navigated away.
  updateNotes: async (projectId: number, notes: string): Promise<boolean> => {
    const detail = useProjectViewStore.getState().detail;
    if (!detail || !detail.project || detail.projectId !== projectId) return false;

    const prevNotes = detail.project.x_dashboard_notes;
    useProjectViewStore.setState({
      detail: { ...detail, project: { ...detail.project, x_dashboard_notes: notes } },
    });

    try {
      await updateProjectNotesApi(projectId, notes);
      return true;
    } catch {
      const currentDetail = useProjectViewStore.getState().detail;
      if (currentDetail && currentDetail.project && currentDetail.projectId === projectId) {
        useProjectViewStore.setState({
          detail: { ...currentDetail, project: { ...currentDetail.project, x_dashboard_notes: prevNotes } },
        });
      }
      return false;
    }
  },

  setActiveTab: (activeTab) => set({ activeTab }),
  setActiveTask: (activeTaskId, auto = false) =>
    set((s) => ({ activeTaskId, taskChoice: auto ? 0 : s.taskChoice + 1 })),
}));
